<template>
  <div
    ref="reference"
    @mouseenter="isOpen = true"
    @mouseleave="isOpen = false"
    :class="triggerClass"
  >
    <slot />
  </div>

  <teleport
    to="body"
    :disabled="!appendToBody"
  >
    <transition name="fade">
      <div
        v-if="isOpen && !disabled"
        ref="floating"
        :style="floatingStyles"
        :class="['app-tooltip', { 'break-all': breakWord }]"
      >
        <p
          v-if="heading"
          :class="['app-tooltip-heading', headingClass]"
        >
          {{ heading }}
        </p>
        <p
          v-if="body"
          :class="['app-tooltip-body', bodyClass]"
        >
          {{ body }}
        </p>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, watchEffect } from 'vue';
import { computePosition, offset as offsetPlugin, flip, shift, autoUpdate } from '@floating-ui/vue';

defineOptions({
  name: 'AppTooltip',
});

const props = defineProps({
  placement: {
    type: String,
    default: 'top',
    validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value),
  },
  offset: {
    type: Number,
    default: 4,
  },
  heading: {
    type: String,
    default: '',
  },
  body: {
    type: String,
    default: '',
  },
  headingClass: {
    type: String,
    default: '',
  },
  bodyClass: {
    type: String,
    default: '',
  },
  triggerClass: {
    type: String,
    default: 'inline-block',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  appendToBody: {
    type: Boolean,
    default: true,
  },
  breakWord: {
    type: Boolean,
    default: false,
  },
});

const reference = ref(null);
const floating = ref(null);
const isOpen = ref(false);
const floatingStyles = ref({});

watchEffect((cleanup) => {
  if (!reference.value || !floating.value || !isOpen.value) return;

  const cleanupAutoUpdate = autoUpdate(reference.value, floating.value, () => {
    computePosition(reference.value, floating.value, {
      placement: props.placement,
      middleware: [offsetPlugin(props.offset), flip(), shift()],
    }).then(({ x, y }) => {
      floatingStyles.value = {
        position: 'absolute',
        left: `${x}px`,
        top: `${y}px`,
      };
    });
  });

  cleanup(() => cleanupAutoUpdate());
});
</script>

<style scoped>
@reference '@/assets/main.css';

.app-tooltip {
  @apply relative max-w-[360px] bg-gray-900 px-3 py-2 rounded-lg font-semibold text-sm text-white z-50;
}

.app-tooltip-heading {
  @apply font-semibold;
}

.app-tooltip-body {
  @apply text-gray-300 font-normal;
}
</style>
